import type { ComponentProps, KeyboardEvent, ReactNode } from 'react';
import { forwardRef } from 'react';

import { cn } from '../lib/cn';

const ARROW_KEYS = new Set(['ArrowDown', 'ArrowUp', 'Home', 'End']);

/**
 * 세로 목록에서 하나만 "지금 선택"인 자리 — 선반 목록 → 인접 상세(E14 §3).
 * ↑·↓·Home·End로 항목 사이를 옮긴다(E14 §14). 선택은 호출자가 쥔다.
 */
export function SelectableList({ className, onKeyDown, children, ...props }: ComponentProps<'ul'>) {
  function handleKeyDown(event: KeyboardEvent<HTMLUListElement>) {
    onKeyDown?.(event);
    if (event.defaultPrevented) return;
    if (!ARROW_KEYS.has(event.key)) return;

    const items = Array.from(
      event.currentTarget.querySelectorAll<HTMLButtonElement>(
        '[data-selectable-list-item]:not(:disabled)',
      ),
    );
    if (items.length === 0) return;

    const lastIndex = items.length - 1;
    const currentIndex = items.indexOf(document.activeElement as HTMLButtonElement);
    const nextIndex =
      event.key === 'Home'
        ? 0
        : event.key === 'End'
          ? lastIndex
          : event.key === 'ArrowDown'
            ? currentIndex < 0
              ? 0
              : (currentIndex + 1) % items.length
            : currentIndex < 0
              ? lastIndex
              : (currentIndex - 1 + items.length) % items.length;

    event.preventDefault();
    items[nextIndex]?.focus();
  }

  return (
    // oxlint-disable-next-line jsx-a11y/no-noninteractive-element-interactions -- 로빙 포커스 위임
    <ul className={cn('flex flex-col', className)} onKeyDown={handleKeyDown} {...props}>
      {children}
    </ul>
  );
}

export interface SelectableListItemProps extends ComponentProps<'button'> {
  /** 지금 인접 상세로 펼쳐진 항목인가 — `aria-current`와 선택 배경을 함께 켠다. */
  selected?: boolean;
  /**
   * 행 끝의 따로 누르는 동작 하나(관심 별 · 빼기). 행 버튼 안이 아니라 옆에 둔다 — 버튼 안에 버튼을 두지 않는다.
   * 아이콘 버튼이면 접근 이름에 행 이름을 담는다.
   */
  action?: ReactNode;
  /** 항목(`li`)의 접근 이름 — 행 안의 글자가 길어 행 버튼 이름으로 찾기 어려울 때 */
  itemLabel?: string;
}

export const SelectableListItem = forwardRef<HTMLButtonElement, SelectableListItemProps>(
  function SelectableListItem(
    { selected = false, action, itemLabel, className, type = 'button', ...props },
    ref,
  ) {
    return (
      <li aria-label={itemLabel} className={action ? 'relative' : undefined}>
        <button
          ref={ref}
          type={type}
          data-selectable-list-item=""
          aria-current={selected ? 'true' : undefined}
          className={cn(
            'px-row py-snug w-full rounded-lg text-left text-sm',
            'ease-flow duration-fast transition-colors',
            // 선택은 brand-soft 바탕 — 네이비 채움은 1차 버튼의 것이다(D150).
            selected ? 'bg-brand-soft text-fg' : 'text-ink-muted hover:bg-highlight',
            'disabled:cursor-default disabled:opacity-60 disabled:hover:bg-transparent',
            action && 'pr-10',
            className,
          )}
          {...props}
        />
        {action ? <div className="absolute top-1 right-1">{action}</div> : null}
      </li>
    );
  },
);
